import { useState, useEffect } from "react";
import { IonModal } from "@ionic/react";
import { Check } from "lucide-react";
import type { ITask } from "../types";

interface CompletionConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The task the user is attempting to complete. */
  task: ITask;
  /** Incomplete prereq tasks (transitive) for `task`. */
  incompletePrereqs: ITask[];
  /** Called with UUIDs of prereq tasks to also complete (may be empty). */
  onConfirm: (cascadePrereqUuids: string[]) => void;
}

export default function CompletionConfirmModal({
  isOpen,
  onClose,
  task,
  incompletePrereqs,
  onConfirm,
}: CompletionConfirmModalProps) {
  // Default: all prereqs checked
  const [checked, setChecked] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      setChecked(new Set(incompletePrereqs.map((t) => t.uuid)));
    }
  }, [isOpen, incompletePrereqs]);

  const toggle = (uuid: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(checked));
    onClose();
  };

  const parentTitle = task.title || task.raw_input;

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      initialBreakpoint={0.6}
      breakpoints={[0, 0.6, 0.9]}
    >
      <div className="p-5 space-y-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-full bg-success/10">
              <Check className="w-4 h-4 text-success" />
            </div>
            <h2 className="text-base font-semibold text-foreground">
              Complete prerequisites too?
            </h2>
          </div>
          <p className="text-xs text-muted-foreground">
            &ldquo;{parentTitle}&rdquo; has prerequisites that aren&rsquo;t done yet.
          </p>
        </div>

        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {incompletePrereqs.map((prereq) => {
            const isChecked = checked.has(prereq.uuid);
            return (
              <label
                key={prereq.uuid}
                className="flex items-start gap-2.5 p-2.5 rounded-lg border border-border bg-surface cursor-pointer hover:bg-muted/40 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggle(prereq.uuid)}
                  className="mt-0.5 w-4 h-4 rounded text-primary focus:ring-primary cursor-pointer"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-foreground truncate">
                    {prereq.title || prereq.raw_input}
                  </p>
                  {prereq.description && (
                    <p className="text-[11px] text-muted-foreground truncate">
                      {prereq.description}
                    </p>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        <p className="text-[11px] text-muted-foreground">
          Dependency links will be removed after completion.
        </p>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="btn-secondary flex-1">
            Cancel
          </button>
          <button onClick={handleConfirm} className="btn-success flex-1">
            Mark {checked.size + 1} done
          </button>
        </div>
      </div>
    </IonModal>
  );
}
